import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Form, Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { QRCodeSVG } from 'qrcode.react'
import { useState } from 'react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { getDomainUrl } from '#app/utils/misc'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

export async function loader({ params, request }: LoaderFunctionArgs) {
	const user = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			createdAt: true,
			image: { select: { id: true } },
			coach_code: true,
		},
		where: {
			username: params.username,
		},
	})

	invariantResponse(user, 'User not found', { status: 404 })

	return json({
		user,
		userJoinedDisplay: user.createdAt.toLocaleDateString(),
		origin: getDomainUrl(request),
	})
}

export default function ProfileRoute() {
	const data = useLoaderData<typeof loader>()
	const user = data.user
	const userDisplayName = user.name ?? user.username
	const loggedInUser = useOptionalUser()
	const isLoggedInUser = data.user.id === loggedInUser?.id
	const [showLargeQR, setShowLargeQR] = useState(false)

	return (
		<div className="container mb-48 mt-36 flex flex-col items-center justify-center">
			<Spacer size="4xs" />

			<div className="container flex flex-col items-center rounded-3xl bg-muted p-12">
				<div className="relative w-52">
					<div className="absolute -top-40">
						<div className="relative">
							<img
								src={getUserImgSrc(data.user.image?.id)}
								alt={userDisplayName}
								className="h-52 w-52 rounded-full object-cover"
							/>
						</div>
					</div>
				</div>

				<Spacer size="sm" />

				<div className="flex items-start justify-between gap-8">
					<div className="flex flex-col items-center">
						<div className="flex flex-wrap items-center justify-center gap-4">
							<h1 className="text-center text-h2">{userDisplayName}</h1>
						</div>
						<p className="mt-2 text-center text-muted-foreground">
							Joined {data.userJoinedDisplay}
						</p>
						{isLoggedInUser ? (
							<Form action="/logout" method="POST" className="mt-3">
								<Button type="submit" variant="link" size="pill">
									<Icon name="exit" className="scale-125 max-md:scale-150">
										Logout
									</Icon>
								</Button>
							</Form>
						) : null}
						<div className="mt-10 flex gap-4">
							{isLoggedInUser ? (
								<>
									<Button asChild>
										<Link to="/settings/profile" prefetch="intent">
											Edit profile
										</Link>
									</Button>
								</>
							) : (
								<Button asChild>
									<Link to="notes" prefetch="intent">
										{userDisplayName}'s notes
									</Link>
								</Button>
							)}
						</div>
					</div>
					{isLoggedInUser ? (
						<div className="flex flex-col items-center gap-3">
							<button
								onClick={() => setShowLargeQR(true)}
								className="transition-transform hover:scale-105"
							>
								<QRCodeSVG
									value={`${data.origin}/vortex/coaches?coach_code=${user.coach_code}`}
									size={200}
								/>
							</button>
							<div className="text-center">
								<p className="text-sm text-muted-foreground">Your Coach Code</p>
								<p className="mt-1 font-mono text-lg font-bold">
									{user.coach_code}
								</p>
								<button
									onClick={() => {
										void navigator.clipboard.writeText(
											`${data.origin}/vortex/coaches?coach_code=${user.coach_code}`,
										)
									}}
									className="mt-2 text-muted-foreground hover:text-foreground"
								>
									<Icon name="arrow-left" />
								</button>
							</div>
						</div>
					) : null}
				</div>
			</div>

			{showLargeQR ? (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
					onClick={() => setShowLargeQR(false)}
				>
					<div className="flex flex-col items-center gap-4 rounded-lg bg-card p-8 shadow-lg">
						<QRCodeSVG
							value={`${data.origin}/vortex/coaches?coach_code=${user.coach_code}`}
							size={400}
						/>
						<p className="font-mono text-2xl font-bold">{user.coach_code}</p>
					</div>
				</div>
			) : null}
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.user.name ?? params.username
	return [
		{ title: `${displayName} | REDX Coaching Dashboard` },
		{
			name: 'description',
			content: `Profile of ${displayName} on REDX Coaching Dashboard`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No user with the username "{params.username}" exists</p>
				),
			}}
		/>
	)
}
